/* eslint-disable react/prop-types */
import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	CircularProgress,
	Box,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import { IVoucherResponse } from '../../services/vouchersService';

interface VoucherDownloadModalProps {
	open: boolean;
	onClose: () => void;
	voucherData: IVoucherResponse[] | null;
	isLoading: boolean;
	error: string | null;
	onForceUpdate?: () => Promise<void>;
}

export const VoucherDownloadModal: React.FC<VoucherDownloadModalProps> = ({
	open,
	onClose,
	voucherData,
	isLoading,
	error,
	onForceUpdate,
}) => {
	const handleDownloadVoucher = (url: string) => {
		window.open(url, '_blank');
	};

	const renderContent = () => {
		if (isLoading) {
			return (
				<Box display="flex" flexDirection="column" alignItems="center" py={3}>
					<CircularProgress size={48} />
					<Typography variant="body1" sx={{ mt: 2 }}>
						Procesando vouchers, por favor aguarde...
					</Typography>
				</Box>
			);
		}

		if (error) {
			return (
				<Alert severity="error" sx={{ my: 2 }}>
					{error}
				</Alert>
			);
		}

		if (!voucherData || voucherData.length === 0) {
			return (
				<Alert severity="warning" sx={{ my: 2 }}>
					No se encontraron datos de vouchers
				</Alert>
			);
		}

		const statusResponse = voucherData[0];
		const dataResponse = voucherData.length > 1 ? voucherData[1] : null;

		// Si el status es "processing"
		if (statusResponse.status === 'processing') {
			return (
				<Alert severity="warning" sx={{ my: 2 }}>
					Procesando vouchers, por favor aguarde 3 minutos e intente nuevamente
				</Alert>
			);
		}

		// Si el status es "ok"
		if (statusResponse.status === 'ok') {
			if (!dataResponse) {
				return (
					<Alert severity="warning" sx={{ my: 2 }}>
						No se encontraron datos de vouchers
					</Alert>
				);
			}

			const hasVouchers = dataResponse.vouchers && dataResponse.vouchers.length > 0;

			if (!hasVouchers) {
				return (
					<Alert severity="info" sx={{ my: 2 }}>
						No hay vouchers disponibles para descargar
					</Alert>
				);
			}

			return (
				<Box>
					<Typography variant="h6" gutterBottom>
						Vouchers disponibles para descarga:
					</Typography>

					{dataResponse.localizer && (
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Localizador: {dataResponse.localizer}
						</Typography>
					)}

					<List>
						{dataResponse.vouchers?.map((voucher: any, index: number) => (
							<ListItem key={voucher.serviceId} sx={{ px: 0 }}>
								<ListItemIcon>
									<DescriptionIcon />
								</ListItemIcon>
								<ListItemText primary={`Voucher ${index + 1}`} secondary={`Service ID: ${voucher.serviceId}`} />
								<Button
									variant="contained"
									size="small"
									startIcon={<DownloadIcon />}
									onClick={() => handleDownloadVoucher(voucher.url)}
								>
									Descargar
								</Button>
							</ListItem>
						))}
					</List>

					{dataResponse.generatedAt && (
						<Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
							Generado: {new Date(dataResponse.generatedAt).toLocaleString()}
						</Typography>
					)}

					{onForceUpdate && (
						<Box sx={{ mt: 2 }}>
							<Button variant="outlined" color="warning" onClick={onForceUpdate} disabled={isLoading} size="small">
								{isLoading ? 'Actualizando...' : 'Forzar actualización'}
							</Button>
						</Box>
					)}
				</Box>
			);
		}

		return (
			<Alert severity="error" sx={{ my: 2 }}>
				Estado de respuesta no reconocido: {statusResponse.status}
			</Alert>
		);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Descarga de Vouchers</DialogTitle>
			<DialogContent>{renderContent()}</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="outlined">
					Cerrar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
